/**
 * "Is the microphone actually picking anything up?" — as a state machine, so the call surface
 * can say so instead of leaving the user talking into a void.
 *
 * A call the agent cannot hear has many causes that are indistinguishable from the caller's
 * side: the browser opened the wrong input, the hardware mute switch is down, the OS denied
 * this app the mic after the browser granted it, a virtual/aggregate device is routed
 * somewhere else. All of them look like "it isn't answering me". None of them are visible in
 * the transcript, and the only tell is the one signal we already have: the input level never
 * leaves zero.
 *
 * The rule that makes it safe: **silence only counts against the mic when it is plausibly the
 * USER'S turn.** While the agent is talking the user is listening, and with echo cancellation
 * their input reads near-zero by design; while a tool/ask is in flight they are waiting. Both
 * are ordinary, both last well past any window worth using, and counting either one is how the
 * hint learned to flap on and off in time with the agent's turns.
 *
 * Deliberately a pure, clock-injected state machine with no audio and no DOM, because the
 * behaviour worth guaranteeing is behaviour ("never while the agent speaks", "never while
 * muted", "clears the instant real audio arrives", "only after a continuous window") rather
 * than any particular rendering.
 */

/**
 * How long the input has to sit at effectively zero, DURING THE USER'S OWN TURN, before we say
 * anything — for a call we have not yet heard a single audible sample on.
 *
 * The old 4s was chosen while agent speech still counted, which made it a de-facto "the agent
 * spoke for four seconds" detector. With the agent's turns and tool waits excluded, what is
 * left is genuine user-turn silence — thinking, reading the screen, someone walking in — and
 * four seconds of that is completely ordinary. Ten is long enough that no ordinary pause
 * reaches it, and still well inside the time a caller spends wondering why nothing answered.
 * A false "your mic is broken" on a working call is much worse than a slow true one: it teaches
 * people to distrust the one hint standing between a deaf call and a baffled user.
 */
export const SILENCE_HINT_AFTER_MS = 10_000;

/**
 * The same window, once this call HAS heard the user audibly at least once.
 *
 * Deliberately not "never again": a hardware mute switch can be flipped, a headset can die or
 * walk out of range, an OS can revoke the grant mid-call — all real, all identical to a dead
 * mic from the caller's side. But a mic that has already been heard is proven hardware, so the
 * leading explanation for a later drought is a person who has gone quiet, not a fault. We
 * demand twice as much evidence before accusing equipment we have watched work.
 */
export const SILENCE_HINT_AFTER_HEARD_MS = 20_000;

/**
 * Below this RMS the input is treated as nothing. A live microphone in a quiet room still
 * carries a room-tone floor well above it; a dead one sits at exactly zero.
 */
export const AUDIBLE_RMS = 0.002;

export interface MicSample {
  /** Latest input RMS, 0..1. */
  rms: number;
  /** Is a call actually running? A hint about a mic nobody is speaking into is noise. */
  live: boolean;
  /** Muted is silence the user chose — saying it looks broken would be a lie. */
  muted: boolean;
  /** The agent is talking. The user is listening, and echo cancellation reads that as zero. */
  agentSpeaking: boolean;
  /** A tool/ask is in flight. The user is waiting on us, not failing to be heard. */
  working: boolean;
  now: number;
}

export class MicSilenceWatch {
  /** When the current run of silence began; null when audio is (or was just) flowing. */
  private silentSince: number | null = null;
  private flagged = false;
  /** Has this call ever produced an audible sample? Decides which window applies. */
  private heardAudio = false;

  /** True while the microphone has been silent long enough to be worth mentioning. */
  get isSilent(): boolean {
    return this.flagged;
  }

  /**
   * Feed one sample. Returns true when the flag CHANGED, so a caller can push state only on a
   * transition rather than on every tick.
   */
  observe({ rms, live, muted, agentSpeaking, working, now }: MicSample): boolean {
    const before = this.flagged;
    if (!live || muted || agentSpeaking || working) {
      // Not a call, silence the user asked for, or silence that isn't theirs to fill. Reset
      // rather than pause, so the user gets a FULL window once their turn actually starts —
      // resuming a part-spent window would fire moments after the agent stops talking.
      this.silentSince = null;
      this.flagged = false;
      return before !== this.flagged;
    }
    if (rms >= AUDIBLE_RMS) {
      // Real audio: clear immediately. Recovery has to be instant — a hint that lingers after
      // the user fixed their mic is worse than one that never appeared.
      this.heardAudio = true;
      this.silentSince = null;
      this.flagged = false;
      return before !== this.flagged;
    }
    if (this.silentSince === null) this.silentSince = now;
    const window = this.heardAudio ? SILENCE_HINT_AFTER_HEARD_MS : SILENCE_HINT_AFTER_MS;
    if (now - this.silentSince >= window) this.flagged = true;
    return before !== this.flagged;
  }

  reset(): void {
    this.silentSince = null;
    this.flagged = false;
    this.heardAudio = false;
  }
}
